import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Repeat } from "lucide-react";
import { type ReactNode, useState } from "react";
import { toast } from "sonner";
import { ApiError, api } from "../../api";
import { useIsAdmin } from "../../lib/permissions";
import type { LoopBudget } from "../../types";
import { TimeAgo } from "../time-ago";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Field, FieldDescription, FieldLabel } from "../ui/field";
import { Input } from "../ui/input";
import { Progress } from "../ui/progress";
import { Spinner } from "../ui/spinner";
import { Textarea } from "../ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "../ui/toggle-group";

const PRESETS = [1, 3, 7];
const SHOWN_GRANTS = 3;

/** Where a grant is sent: the issue page addresses the work order through its issue. */
export interface LoopTarget {
  taskId: string;
  issueRef?: string;
}

/** Most loops one grant can add right now: the per-grant cap, or what is left under the ceiling. */
export function loopsAddable(budget: LoopBudget): number {
  return Math.max(0, Math.min(budget.maxGrant, budget.ceiling - budget.allowed));
}

/**
 * Verifier → Fixer loops used on a work order against its budget, the grants
 * admins made, and (for admins, while the work order is open) Add loops.
 */
export function LoopBudgetRow({
  budget,
  target,
  open,
  onChanged,
}: {
  budget: LoopBudget;
  target: LoopTarget;
  open: boolean;
  onChanged?: () => void;
}) {
  const admin = useIsAdmin();
  const spent = budget.remaining === 0;
  const grants = [...budget.grants].reverse();
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="inline-flex items-center gap-1.5 text-muted-foreground">
          <Repeat className="size-3.5" aria-hidden="true" />
          Fixer loops
        </span>
        <span className="flex items-center gap-2">
          <span
            className={spent ? "font-medium text-amber-700 tabular-nums dark:text-amber-300" : "tabular-nums"}
          >
            {budget.used} of {budget.allowed} used
          </span>
          {admin && open && (
            <AddLoopsDialog
              budget={budget}
              target={target}
              onChanged={onChanged}
              trigger={
                <Button size="xs" variant="ghost" className="px-1.5">
                  <Plus className="size-3" /> Add loops
                </Button>
              }
            />
          )}
        </span>
      </div>
      <Progress
        aria-label={`${budget.used} of ${budget.allowed} Fixer loops used`}
        value={budget.allowed === 0 ? 100 : Math.min(100, (budget.used / budget.allowed) * 100)}
        className={spent ? "[&>[data-slot=progress-indicator]]:bg-amber-500" : undefined}
      />
      {grants.length > 0 && (
        <ul className="space-y-0.5 text-xs text-muted-foreground">
          {grants.slice(0, SHOWN_GRANTS).map((grant) => (
            <li key={grant.id} className="truncate" title={grant.reason ?? undefined}>
              <span className="font-medium text-foreground tabular-nums">+{grant.amount}</span> by{" "}
              {grant.grantedBy} · <TimeAgo at={grant.createdAt} />
              {grant.reason && <> · {grant.reason}</>}
            </li>
          ))}
          {grants.length > SHOWN_GRANTS && (
            <li>
              and {grants.length - SHOWN_GRANTS} earlier grant{grants.length - SHOWN_GRANTS === 1 ? "" : "s"}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

/**
 * Admin-only grant of extra loops. When the work order is held because its
 * budget ran out, the grant also sends the Verifier's findings to the Fixer.
 */
export function AddLoopsDialog({
  budget,
  target,
  trigger,
  held = false,
  onChanged,
}: {
  budget: LoopBudget;
  target: LoopTarget;
  trigger: ReactNode;
  held?: boolean;
  onChanged?: () => void;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const max = loopsAddable(budget);
  const [amount, setAmount] = useState(String(Math.min(held ? 1 : 3, Math.max(1, max))));
  const [reason, setReason] = useState("");
  const add = Number(amount);
  const valid = Number.isInteger(add) && add >= 1 && add <= max;

  const grant = useMutation({
    mutationFn: () => {
      const input = { add, reason: reason.trim() || undefined };
      return target.issueRef
        ? api.grantIssueLoops(target.issueRef, input)
        : api.grantTaskLoops(target.taskId, input);
    },
    onSuccess: ({ loopBudget, run }) => {
      setOpen(false);
      setReason("");
      toast.success(`Added ${add} loop${add === 1 ? "" : "s"}`, {
        description: run
          ? "The Verifier's findings went back to the Fixer."
          : `${loopBudget.remaining} of ${loopBudget.allowed} loops left on this work order.`,
      });
      void queryClient.invalidateQueries({ queryKey: ["task", target.taskId] });
      void queryClient.invalidateQueries({ queryKey: ["tasks"] });
      void queryClient.invalidateQueries({ queryKey: ["state"] });
      onChanged?.();
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Could not add loops"),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!grant.isPending) setOpen(next);
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <form
          className="grid gap-6"
          onSubmit={(event) => {
            event.preventDefault();
            if (valid && !grant.isPending) grant.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>Add Fixer loops</DialogTitle>
            <DialogDescription>
              Each loop lets the Verifier send its findings back to the Fixer once more. This work order has
              used {budget.used} of {budget.allowed}.
              {held &&
                " It is held because the budget ran out, so adding loops sends it back to the Fixer now."}
            </DialogDescription>
          </DialogHeader>
          {max === 0 ? (
            <p className="text-sm text-muted-foreground">
              This work order is at the {budget.ceiling}-loop ceiling. Continue it by hand or pull it from the
              line.
            </p>
          ) : (
            <>
              <Field>
                <FieldLabel htmlFor="loop-grant-amount">Loops to add</FieldLabel>
                <div className="flex items-center gap-2">
                  <ToggleGroup
                    type="single"
                    variant="outline"
                    size="sm"
                    value={PRESETS.includes(add) ? String(add) : ""}
                    onValueChange={(value) => value && setAmount(value)}
                  >
                    {PRESETS.filter((preset) => preset <= max).map((preset) => (
                      <ToggleGroupItem key={preset} value={String(preset)} aria-label={`Add ${preset}`}>
                        +{preset}
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>
                  <Input
                    id="loop-grant-amount"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={max}
                    step={1}
                    value={amount}
                    onChange={(event) => setAmount(event.target.value)}
                    className="h-8 w-20 tabular-nums"
                  />
                </div>
                <FieldDescription>
                  {valid
                    ? `New budget: ${budget.allowed + add} loops, ${budget.allowed + add - budget.used} left.`
                    : `Add between 1 and ${max} loops.`}
                </FieldDescription>
              </Field>
              <Field>
                <FieldLabel htmlFor="loop-grant-reason">Reason (optional)</FieldLabel>
                <Textarea
                  id="loop-grant-reason"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="For example: let it keep going overnight."
                  maxLength={500}
                  rows={2}
                />
              </Field>
            </>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={grant.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!valid || grant.isPending}>
              {grant.isPending && <Spinner />}
              {valid ? `Add ${add} loop${add === 1 ? "" : "s"}` : "Add loops"}
              {held && valid && " and continue"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
